import { Modal } from "antd";
import { callApi } from "./superAgentUtil";

export interface DeletableInfo {
    blocked: boolean;
    count: number;
    label: string;
    message?: string;
}

/** Generic "can't delete" popup (only a Close button) — the shared block UX for every reason. */
export function showBlockedModal(opts: { title: string; content: string }) {
    Modal.confirm({
        title: opts.title,
        content: opts.content,
        okButtonProps: { style: { display: "none" } },
        cancelText: "Close",
    });
}

/**
 * Ask the backend whether an entity can be deleted (i.e. no booking references it).
 * Fails OPEN: on any error it returns not-blocked, so the delete still proceeds and the
 * backend guard remains the hard safety net.
 */
export async function checkEntityDeletable(firebase: any, type: string, id: string): Promise<DeletableInfo> {
    try {
        const res = await callApi(firebase, "get", `bookingoApi/package/deletable/${type}/${id}`, {});
        if (res && res.status === 200 && res.body && res.body.data) {
            return res.body.data as DeletableInfo;
        }
    } catch (e) {
        // swallow — fail open
    }
    return { blocked: false, count: 0, label: type };
}

/** Show the "can't delete — N bookings reference it" modal (no delete action). */
export function showDeletionBlockedModal(info: DeletableInfo) {
    // Prefer the backend-built message (accurate per-entity suggestion); fall back if absent.
    const content = info.message
        || `This ${info.label} can't be deleted because ${info.count} booking(s) reference it. Deleting it would leave those bookings incomplete.`;
    showBlockedModal({ title: `Can't delete this ${info.label}`, content });
}

/**
 * Proactive guarded delete: checks deletability first. If a booking references the entity,
 * shows the blocked modal and stops; otherwise calls `proceed()` (the entity's normal
 * delete-confirm + dispatch).
 */
export async function guardedDelete(firebase: any, type: string, id: string, proceed: () => void): Promise<void> {
    const info = await checkEntityDeletable(firebase, type, id);
    if (info.blocked) {
        showDeletionBlockedModal(info);
        return;
    }
    proceed();
}
